import { MyProjects } from '@platform/client';
import {
  ProCard,
  ProCardProject,
  ProjectPublishingStage,
  ProSavedProjectCard,
} from '@platform/client/schemas';
import { AlertIconTooltip, Card, CardBadge } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { useSearch } from 'wouter';
import { ProjectType } from '.';
import dataTestids from '../../constants/test-ids';
import { IntroductionModalContext } from './Modals/IntroductionModal';
import {
  formatDateStr,
  getButtonProps,
  getCardStatusBadge,
  getHeaderContent,
  getSubtext,
} from './utils';

export const ProUserCard = ({ cardData }: { cardData: ProCard }) => {
  const { cardStatus, project, organization } = cardData;

  const { openIntroductionModal } = useContext(IntroductionModalContext);

  // Open modal if introductionId is in the URL param
  const searchParams = useSearch();
  const urlSearchParams = new URLSearchParams(searchParams);
  const paramMatchGroupId = parseInt(urlSearchParams.get('matchGroupId') ?? '');
  useEffect(() => {
    if (paramMatchGroupId && paramMatchGroupId === cardData.matchGroupId) {
      openIntroductionModal({
        userType: 'experteer',
        cardData: cardData,
        project: project,
      });
    }
  }, [paramMatchGroupId, cardData, openIntroductionModal, project]);

  const headerProps = getHeaderContent({ cardData, userType: 'pro' });
  const badges: CardBadge[] = [getCardStatusBadge(cardStatus)];
  if (project?.roleType) {
    badges.unshift({
      text: ProjectType[project.roleType],
      color: 'secondary-brand-25',
    });
  } else {
    badges.unshift({
      text: 'Custom',
      color: 'secondary-brand-25',
    });
  }
  const subText = getSubtext({ cardData, userType: 'pro' });

  const buttonProps = getButtonProps({
    openIntroModal: () =>
      openIntroductionModal({
        userType: 'experteer',
        cardData: cardData,
        project: project,
      }),
    cardData,
    userType: 'pro',
  });

  return (
    <Card
      headerContent={headerProps.headerText}
      headerColor={headerProps.headerColor}
      buttonText={buttonProps?.buttonText}
      buttonVariant={buttonProps?.buttonVariant}
      buttonOnClick={buttonProps?.buttonCta}
      content={CardContent(project)}
      contentTextHref={project?.id ? `/platform/directory/projects/${project.id}` : undefined}
      contentSubText={organization.name}
      contentSubtextHref={`/platform/directory/organizations/${organization.id}`}
      avatarSize='md'
      avatarHref={cardData.avatar || ''}
      badges={badges}
      variant='large'
      footerText={subText}
      userOrOrg='organization'
      dataTestId={`${dataTestids.MY_PROJECTS.PROJECT_CARD.CONTAINER}-${project?.id ?? 'custom-' + organization.id}`}
    />
  );
};

export const ProUserSavedProjectCard = ({ cardData }: { cardData: ProSavedProjectCard }) => {
  const { project, organization } = cardData;
  const queryClient = useQueryClient();
  const published = project.publishingStage === ProjectPublishingStage.published;
  // Setting projectUrl to undefined will properly set button to disabled since defined Href will create a link as button
  const projectUrl = published ? `/platform/directory/projects/${project.id}` : undefined;

  const unsaveProjectMutation = MyProjects.useUnsaveProject({
    mutation: {
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetProUserMyProjectsQueryKey() });
      },
    },
  });

  return (
    <Card
      headerContent={undefined}
      headerColor={'gray-50'}
      buttonText={published ? 'See Details' : 'Unavailable'}
      buttonVariant={'secondary-gray'}
      buttonHref={projectUrl}
      buttonDisabled={!published}
      content={project.name}
      contentTextHref={projectUrl}
      contentSubText={organization.name}
      contentSubtextHref={`/platform/directory/organizations/${organization.id}`}
      avatarSize='md'
      avatarHref={cardData.avatar || ''}
      badges={[
        {
          text: ProjectType[project.roleType],
          color: 'secondary-brand-25',
        },
      ]}
      variant='large'
      footerText={`Saved ${formatDateStr(cardData.savedAt)}`}
      footerLinkText={'Unsave'}
      footerLinkOnClick={() => {
        unsaveProjectMutation.mutate({
          params: {
            project_id: project.id,
          },
        });
      }}
      userOrOrg='organization'
      dataTestId={`${dataTestids.MY_PROJECTS.PROJECT_CARD.CONTAINER}-${project.id}`}
    />
  );
};

const CardContent = (project: ProCardProject) => {
  return !project?.name ?
      <div className='flex justify-left gap-2 items-center text-md'>
        {`Custom project`}
        <div className='hidden md:inline mt-2'>
          <AlertIconTooltip
            size='md'
            placement='bottom end'
            header="What's a custom project?"
            supportingText='This organization is interested in collaborating with you to create a project that is a good fit for both of you. If you connect, you can discuss more of these details in your discovery call. Once you match, you can update the project name.'
          />
        </div>
      </div>
    : <div>{project?.name}</div>;
};
